import { API, BlockTool } from "@editorjs/editorjs"

/**
 * The data object used to store the tip contents.
 */
export interface AlertData {
    /** The text content of the alert. */
    text?: string;

    /** The type of alert. */
    type?: "tip" | "note" | "important" | "warning" | "caution"
}

/**
 * The configuration options for the alert tool.
 */
export interface AlertConfig {
}

/**
 * The definition of what a style type looks like.
 */
interface StyleType {
    /** The class string to be assigned to the icon. */
    iconClass: string;

    /** The class applied to the alert itself. */
    class: string;

    /** The title text displayed at the top of the alert. */
    text: string;
};

const Styles: Array<StyleType> = [
    { iconClass: "fa fa-lightbulb", class: "alert-success", text: "Tip" },
    { iconClass: "fa fa-info-circle", class: "alert-info", text: "Note" },
    { iconClass: "fa fa-exclamation-circle", class: "alert-info", text: "Important" },
    { iconClass: "fa fa-exclamation-triangle", class: "alert-warning", text: "Warning" },
    { iconClass: "fa fa-times-circle", class: "alert-danger", text: "Caution" },
];

require("./index.css").toString();

/**
 * EditorJS tool for displaying quick tips and alerts to the user.
 */
export class Alert implements BlockTool {
    /** Reference to the EditorJS API for this instance. */
    private api: API;

    /** The configuration data we were initialized with. */
    private config: AlertConfig;

    /** True if we were initialized as a read only block. */
    private readOnly: boolean;

    /** The wrapper HTML element that contains all our UI data. */
    private wrapper!: HTMLDivElement;

    /** The paragraph element for the user to enter their text into. */
    private content!: HTMLParagraphElement;

    /**
     * Creates a new instance of the Alert block tool.
     * 
     * @param config The initialization options for this tool.
     */
    constructor(config: { data: AlertData, config: AlertConfig, api: API, readOnly: boolean }) {
        this.api = config.api;
        this.config = config.config;
        this.readOnly = config.readOnly;

        this.setupContent(config.data);
    }

    /**
     * Enable Conversion Toolbar. Paragraph can be converted to/from other tools
     */
    static get conversionConfig() {
        return {
            export: 'text', // to convert Alert to other block, use 'text' property of saved data
            import: 'text' // to covert other block's exported string to Alert, fill 'text' property of tool data
        };
    }

    /**
     * Return the information to create the toolbox icon.
     */
    static get toolbox() {
        return {
            title: 'Alert',
            icon: "<i class=\"fa fa-info-circle\"></i>"
        };
    }

    /**
     * Setup all our DOM elements so we are ready to display data.
     * 
     * @param data The data to load from a previous session.
     */
    setupContent(data: AlertData) {
        var styles = Styles.filter(a => a.text.toLowerCase() === data.type);
        let style: StyleType;

        if (styles.length >= 1) {
            style = styles[0];
        }
        else {
            style = Styles.filter(a => a.text.toLowerCase() === "note")[0];
        }

        this.wrapper = document.createElement("div");
        this.wrapper.classList.add("ce-alert", this.api.styles.block, "alert", style.class);

        const title = document.createElement("p");
        title.classList.add("alert-title");
        title.innerHTML = `<i class="${style.iconClass}"></i> ${style.text}`;
        this.wrapper.appendChild(title);

        this.content = document.createElement("p");
        this.content.classList.add("ce-alert-content");
        this.content.innerHTML = data.text || "";
        this.wrapper.appendChild(this.content);

        if (!this.readOnly) {
            this.content.contentEditable = "true";
            this.content.addEventListener('keyup', this.onKeyUp.bind(this));
        }
    }

    /**
     * Check if text content is empty and set empty string to inner html.
     * We need this because some browsers (e.g. Safari) insert <br> into empty contenteditable elements
     *
     * @param e Key up event
     */
    onKeyUp(e: KeyboardEvent) {
        if (e.code !== 'Backspace' && e.code !== 'Delete') {
            return;
        }

        const { textContent } = this.content;

        if (textContent === '') {
            this.content.innerHTML = '';
        }
    }

    /**
     * Returns the DOM element taht contains all our on-screen information.
     */
    render() {
        return this.wrapper;
    }

    /**
     * Create Block's settings block
     *
     * @returns The HTML element that contains our custom settings buttons.
     */
    renderSettings() {
        const holder = document.createElement('DIV');

        const styleButtons: Array<HTMLElement> = [];

        /** Add type selectors */
        Styles.forEach(style => {
            const selectTypeButton = document.createElement('SPAN');

            selectTypeButton.classList.add(this.api.styles.settingsButton);

            /**
             * Highlight current button
             */
            if (this.wrapper.querySelector("p.alert-title > i")!.className == style.iconClass) {
                selectTypeButton.classList.add(this.api.styles.settingsButtonActive);
            }

            /**
             * Add SVG icon
             */
            selectTypeButton.innerHTML = `<i class="${style.iconClass}"></i>`;

            /**
             * Save info to its button
             */
            selectTypeButton.dataset.iconClass = style.iconClass;

            /**
             * Set up click handler
             */
            selectTypeButton.addEventListener('click', () => {
                this.wrapper.classList.remove("alert-success", "alert-info", "alert-warning", "alert-danger");
                this.wrapper.classList.add(style.class);
                this.wrapper.querySelector("p.alert-title")!.innerHTML = `<i class="${style.iconClass}"></i> ${style.text}`;

                styleButtons.forEach(button => {
                    button.classList.toggle(this.api.styles.settingsButtonActive, button.dataset.iconClass === style.iconClass);
                });
            });

            /**
             * Append settings button to holder
             */
            holder.appendChild(selectTypeButton);
            styleButtons.push(selectTypeButton);
        });

        return holder;
    }

    /**
     * Method that specified how to merge two Text blocks.
     * Called by Editor.js by backspace at the beginning of the Block
     * @param data The data being merged in with ours.
     */
    merge(data: AlertData) {
        this.content.innerHTML = this.content.innerHTML + (data.text || "") ;
    }

    /**
     * Returns the data that describes this block in enough detail that we can
     * reconstruct it later.
     * 
     * @param blockContent The DOM element that was previously returned by render.
     */
    save(blockContent: HTMLDivElement) {
        const iconClass = this.wrapper.querySelector("p.alert-title > i")!.className;

        let styles = Styles.filter(a => a.iconClass === iconClass);
        let style = styles.length >= 1 ? styles[0] : Styles.filter(a => a.text.toLowerCase() === "note")[0];

        let data: AlertData = {
            text: this.content.innerHTML,
            type: <any>style.text.toLowerCase()
        };

        return data;
    }

    /**
     * Validate data to make sure the text isn't blank.
     *
     * @param savedData Data generated after saving.
     * @returns false if saved data is not correct, otherwise true.
     * @public
     */
    validate(savedData: AlertData) {
        if (savedData.text === undefined || savedData.text.trim() === "") {
            return false;
        }

        return true;
    }
}
